import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Select } from '../components/common/Select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/common/Tabs';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { useAuth } from '../context/AuthContext';
import {
    Bell,
    CreditCard,
    Lock,
    User,
    DollarSign,
    Calendar,
    BarChart,
    AlertCircle
} from 'lucide-react';
import Feedback from '../components/common/Feedback';
import { handleFeedbackSubmit } from '../utils/feedback';
import api from '../config/api';
import endpoints from '../config/endpoints';
import { CURRENCIES } from '../config/currencies';

export const Settings = () => {
    const { user, updateUserProfile } = useAuth();

    const [formData, setFormData] = useState({
        name: user?.name || '',
        email: user?.email || '',
        currency: user?.preferences?.currency || 'USD',
        budget: user?.preferences?.budget || '',
        reminderPeriod: '1-day',
        notifications: {
            push: user?.preferences?.notification_settings?.push_notifications ?? true,
            email: user?.preferences?.notification_settings?.email_reminders ?? true,
            report: false,
            payment: true
        }
    });

    useEffect(() => {
        if (user) {
            setFormData(prevData => ({
                ...prevData,
                name: user.name || '',
                email: user.email || '',
                currency: user.preferences?.currency || prevData.currency,
                budget: user.preferences?.budget || prevData.budget,
                notifications: {
                    ...prevData.notifications,
                    push: user.preferences?.notification_settings?.push_notifications ?? prevData.notifications.push,
                    email: user.preferences?.notification_settings?.email_reminders ?? prevData.notifications.email,
                }
            }));
        }
    }, [user]);

    // Helper function to get user initials
    const getUserInitials = () => {
        if (user?.displayName) {
            // Get initials from display name (e.g., "John Doe" -> "JD")
            return user.displayName
                .split(' ')
                .map(name => name[0])
                .join('')
                .toUpperCase()
                .slice(0, 2);
        }
        if (user?.email) {
            // If no display name, use first letter of email
            return user.email[0].toUpperCase();
        }
        // Fallback
        return 'DS';
    };

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    // Split the update handlers to be more specific
    const handleProfileUpdate = async () => {
        try {
            setIsLoading(true);
            setError(null);

            // Only send name update to profile endpoint
            const response = await api.put(endpoints.user.updateProfile, {
                name: formData.name
            });

            if (response.data) {
                await updateUserProfile();
                setSuccessMessage('Profile updated successfully!');
            } else {
                throw new Error('No response data received');
            }
        } catch (err) {
            console.error('Profile update error:', err);
            setError(err.response?.data?.message || err.message || 'Failed to update profile');
        } finally {
            setIsLoading(false);
        }
    };

    // Add new handler for combined updates
    const handleCombinedUpdate = async () => {
        try {
            setIsLoading(true);
            setError(null);

            // Prepare preferences payload with all existing values
            const preferencesPayload = {
                currency: formData.currency,
                budget: formData.budget ? parseFloat(formData.budget) : user?.preferences?.budget,
                timezone: user?.preferences?.timezone || 'UTC',
                notification_settings: {
                    email_reminders: formData.notifications.email,
                    push_notifications: formData.notifications.push,
                    report_frequency: user?.preferences?.notification_settings?.report_frequency,
                    payment_reminders: user?.preferences?.notification_settings?.payment_reminders,
                    // Preserve any other existing notification settings
                    ...user?.preferences?.notification_settings,
                    // Override with new values
                    email_reminders: formData.notifications.email,
                    push_notifications: formData.notifications.push,
                },
                // Preserve any other existing preferences
                ...user?.preferences,
                // Override with new values
                currency: formData.currency,
                budget: formData.budget ? parseFloat(formData.budget) : user?.preferences?.budget,
            };

            // Call both APIs in parallel
            const [profileResponse, preferencesResponse] = await Promise.all([
                api.put(endpoints.user.updateProfile, {
                    name: formData.name
                }),
                api.put(endpoints.user.updatePreferences, 
                    user?.is_anonymous 
                        ? { 
                            currency: formData.currency,
                            budget: formData.budget ? parseFloat(formData.budget) : undefined
                        }
                        : preferencesPayload
                )
            ]);

            if (profileResponse.data && preferencesResponse.data) {
                await updateUserProfile();
                setSuccessMessage('Settings updated successfully!');
            }
        } catch (err) {
            console.error('Settings update error:', err);
            setError(err.response?.data?.message || err.message || 'Failed to update settings');
        } finally {
            setIsLoading(false);
        }
    };

    // Clear success message after 3 seconds
    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage('');
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const handlePasswordChange = async () => {
        try {
            // Validate passwords match
            if (passwordForm.newPassword !== passwordForm.confirmPassword) {
                setError('New passwords do not match');
                return;
            }

            setIsLoading(true);
            setError(null);

            const response = await api.post(endpoints.auth.resetPassword, {
                current_password: passwordForm.currentPassword,
                new_password: passwordForm.newPassword
            });

            if (response.data) {
                setSuccessMessage('Password updated successfully!');
                // Clear password form
                setPasswordForm({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });
            } else {
                throw new Error('No response data received');
            }
        } catch (err) {
            console.error('Password update error:', err);
            setError(
                err.response?.data?.message ||
                err.message ||
                'Failed to update password'
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handlePasswordFormChange = (e) => {
        const { name, value } = e.target;
        setPasswordForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // In the JSX, add the budget field in both anonymous and logged-in user views
    // For anonymous users, add this inside the existing CardContent:
    const budgetField = (
        <div className="space-y-4">
            <h3 className="font-semibold">Monthly Budget</h3>
            <div className="flex items-center space-x-2">
                <DollarSign className="h-5 w-5 text-gray-500" />
                <Input
                    type="number"
                    name="budget"
                    value={formData.budget}
                    onChange={handleInputChange}
                    placeholder="Enter your monthly budget"
                    step="0.01"
                    min="0"
                    className="flex-1"
                />
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Header />
            <main className="flex-1 pt-16">
                <div className="container mx-auto py-10 px-4 max-w-4xl">
                    <div className="flex items-center justify-between mb-8">
                        <h1 className="text-3xl font-bold">Settings</h1>
                        <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
                            <span className="text-lg font-medium">
                                {getUserInitials()}
                            </span>
                        </div>
                    </div>

                    {(error || successMessage) && (
                        <div className={`mb-4 p-4 rounded-md ${error ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>
                            {error || successMessage}
                        </div>
                    )}

                    {user?.is_anonymous ? (
                        <Card>
                            <CardHeader>
                                <CardTitle>Limited Settings Access</CardTitle>
                                <p className="text-sm text-gray-500">
                                    You currently have access to basic currency settings. Create an account to unlock all features:
                                </p>
                                <ul className="mt-2 space-y-1 text-sm text-gray-500 list-disc list-inside">
                                    <li>Customize notification preferences</li>
                                    <li>Set up subscription reminders</li>
                                    <li>Manage security settings</li>
                                    <li>Access detailed reports</li>
                                </ul>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="space-y-4">
                                    <h3 className="font-semibold">Default Currency</h3>
                                    <div className="flex items-center space-x-2">
                                        <DollarSign className="h-5 w-5 text-gray-500" />
                                        <Select
                                            name="currency"
                                            value={formData.currency}
                                            onChange={handleInputChange}
                                            className="flex-1"
                                        >
                                            {CURRENCIES.map(currency => (
                                                <option key={currency.code} value={currency.code}>
                                                    {currency.code} ({currency.symbol}) - {currency.name}
                                                </option>
                                            ))}
                                        </Select>
                                    </div>
                                    {budgetField}
                                    <div className="flex gap-4">
                                        <Button
                                            onClick={handleCombinedUpdate}
                                            disabled={isLoading}
                                            className="w-full sm:w-auto"
                                        >
                                            {isLoading ? 'Saving...' : 'Save Preferences'}
                                        </Button>
                                        <Button
                                            variant="outline"
                                            onClick={() => window.location.href = '/signup'}
                                            className="w-full sm:w-auto flex items-center gap-2"
                                        >
                                            <User className="h-4 w-4" />
                                            Create Free Account
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ) : (
                        <Tabs defaultValue="account" className="space-y-6">
                            <TabsList>
                                <TabsTrigger value="account" className="flex flex-col items-center p-4">
                                    <User className="h-5 w-5 mb-2" />
                                    Account
                                </TabsTrigger>
                                <TabsTrigger value="subscriptions" className="flex flex-col items-center p-4">
                                    <CreditCard className="h-5 w-5 mb-2" />
                                    Subscriptions
                                </TabsTrigger>
                                <TabsTrigger value="notifications" className="flex flex-col items-center p-4">
                                    <Bell className="h-5 w-5 mb-2" />
                                    Notifications
                                </TabsTrigger>
                                <TabsTrigger value="security" className="flex flex-col items-center p-4">
                                    <Lock className="h-5 w-5 mb-2" />
                                    Security
                                </TabsTrigger>
                            </TabsList>

                            <TabsContent value="account">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Account Information</CardTitle>
                                        <p className="text-sm text-gray-500">
                                            Manage your account details and preferences.
                                        </p>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        <div className="space-y-4">
                                            <h3 className="font-semibold">Default Currency</h3>
                                            <div className="flex items-center space-x-2">
                                                <DollarSign className="h-5 w-5 text-gray-500" />
                                                <Select
                                                    name="currency"
                                                    value={formData.currency}
                                                    onChange={handleInputChange}
                                                    className="flex-1"
                                                >
                                                    {CURRENCIES.map(currency => (
                                                        <option key={currency.code} value={currency.code}>
                                                            {currency.code} ({currency.symbol}) - {currency.name}
                                                        </option>
                                                    ))}
                                                </Select>
                                            </div>
                                            {budgetField}
                                        </div>
                                        <hr className="border-gray-200" />
                                        <div className="grid gap-4 sm:grid-cols-2">
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium">Name</label>
                                                <Input
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter your name"
                                                    disabled={isLoading}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium">Email</label>
                                                <Input
                                                    name="email"
                                                    type="email"
                                                    value={formData.email}
                                                    disabled={true}
                                                    className="bg-gray-50 cursor-not-allowed"
                                                />
                                            </div>
                                        </div>
                                        <Button
                                            onClick={handleCombinedUpdate}
                                            disabled={isLoading}
                                            className="w-full sm:w-auto"
                                        >
                                            {isLoading ? 'Saving...' : 'Save Changes'}
                                        </Button>
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            <TabsContent value="subscriptions">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Subscription Preferences</CardTitle>
                                        <p className="text-sm text-gray-500">
                                            Customize how you track and manage your subscriptions.
                                        </p>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        <div className="space-y-4">
                                            <h3 className="font-semibold">Renewal Reminders</h3>
                                            <div className="flex items-center space-x-2">
                                                <Calendar className="h-5 w-5 text-gray-500" />
                                                <Select
                                                    name="reminderPeriod"
                                                    value={formData.reminderPeriod}
                                                    onChange={handleInputChange}
                                                    className="flex-1"
                                                >
                                                    <option value="1-day">1 day before</option>
                                                    <option value="3-days">3 days before</option>
                                                    <option value="1-week">1 week before</option>
                                                </Select>
                                            </div>
                                        </div>
                                        <Button
                                            onClick={handleCombinedUpdate}
                                            disabled={isLoading}
                                            className="w-full sm:w-auto"
                                        >
                                            {isLoading ? 'Updating...' : 'Update Preferences'}
                                        </Button>
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            <TabsContent value="notifications">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Notification Settings</CardTitle>
                                        <p className="text-sm text-gray-500">
                                            Manage how you receive alerts about your subscriptions.
                                        </p>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        <div className="space-y-4">
                                            {[
                                                { icon: Bell, label: "Push Notifications", id: "push" },
                                                { icon: AlertCircle, label: "Email Alerts", id: "email" },
                                                { icon: BarChart, label: "Monthly Report", id: "report" },
                                                { icon: CreditCard, label: "Payment Reminders", id: "payment" }
                                            ].map((item) => (
                                                <div key={item.id} className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-2">
                                                        <item.icon className="h-5 w-5 text-gray-500" />
                                                        <label className="text-sm font-medium">{item.label}</label>
                                                    </div>
                                                    <input
                                                        type="checkbox"
                                                        id={item.id}
                                                        checked={formData.notifications[item.id]}
                                                        onChange={(e) => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                notifications: {
                                                                    ...prev.notifications,
                                                                    [item.id]: e.target.checked
                                                                }
                                                            }));
                                                        }}
                                                        className="rounded border-gray-300"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                        <Button>Save Notification Settings</Button>
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            <TabsContent value="security">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Security Settings</CardTitle>
                                        <p className="text-sm text-gray-500">
                                            Manage your account's security and access.
                                        </p>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        <div className="space-y-4">
                                            <h3 className="font-semibold">Change Password</h3>
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium">Current Password</label>
                                                <Input
                                                    type="password"
                                                    name="currentPassword"
                                                    value={passwordForm.currentPassword}
                                                    onChange={handlePasswordFormChange}
                                                    disabled={isLoading}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium">New Password</label>
                                                <Input
                                                    type="password"
                                                    name="newPassword"
                                                    value={passwordForm.newPassword}
                                                    onChange={handlePasswordFormChange}
                                                    disabled={isLoading}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-sm font-medium">Confirm New Password</label>
                                                <Input
                                                    type="password"
                                                    name="confirmPassword"
                                                    value={passwordForm.confirmPassword}
                                                    onChange={handlePasswordFormChange}
                                                    disabled={isLoading}
                                                />
                                            </div>
                                            <Button
                                                onClick={handlePasswordChange}
                                                disabled={isLoading || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
                                                className="w-full sm:w-auto"
                                            >
                                                {isLoading ? 'Updating...' : 'Change Password'}
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            </TabsContent>
                        </Tabs>
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );
}; 